/**
 * Design system do Kaizen (10.5). Os estilos reproduzem os quadros do design (new_app/kaizen-design),
 * sempre pelos tokens de cor da 10.4.
 */
import type { AlertTone } from '@kaizen/shared';
import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
  forwardRef,
} from 'react';
import { Link } from 'react-router-dom';

export const serif: CSSProperties = { fontFamily: 'var(--font-serif)' };

/** Kanji decorativo (aria-hidden, lang=ja). */
export function Kanji({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span lang="ja" aria-hidden="true" style={{ fontFamily: 'var(--font-serif)', ...style }}>
      {children}
    </span>
  );
}

/** Logo 改 + Kaizen (topo do Hoje). */
export function Logo({ size = 32 }: { size?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span
        lang="ja"
        aria-hidden="true"
        style={{
          width: size,
          height: size,
          borderRadius: 5,
          background: 'var(--shu)',
          color: 'var(--paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-serif)',
          fontSize: Math.round(size * 0.6),
          fontWeight: 800,
        }}
      >
        改
      </span>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 800 }}>Kaizen</span>
    </div>
  );
}

/** Título de página: "Perfil 私" (40 px + kanji 17 px vermelhão). */
export function PageTitle({ title, kanji, kanjiColor = 'var(--shu)', size = 40 }: {
  title: ReactNode; kanji?: string; kanjiColor?: string; size?: number;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
      <h1 style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: size, lineHeight: 1.05, fontWeight: 800 }}>
        {title}
      </h1>
      {kanji && (
        <span lang="ja" style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 700, color: kanjiColor }}>
          {kanji}
        </span>
      )}
    </div>
  );
}

/** "‹ Hoje" — voltar. */
export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      style={{
        alignSelf: 'flex-start',
        minHeight: 36,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 13,
        lineHeight: 1.4,
        fontWeight: 700,
        color: 'var(--muted)',
        textDecoration: 'none',
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" />
      </svg>
      {children}
    </Link>
  );
}

/** Cabeçalho de seção: 12 px, 700, ls 2 px, maiúsculas, linha que ocupa o resto. */
export function SectionHeader({
  children, kanji, kanjiColor = 'var(--ink)', right, style, id,
}: {
  children: ReactNode; kanji?: string; kanjiColor?: string; right?: ReactNode; style?: CSSProperties; id?: string;
}) {
  return (
    <h2
      id={id}
      style={{
        margin: '28px 20px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 2,
        color: 'var(--muted)',
        textTransform: 'uppercase',
        ...style,
      }}
    >
      {kanji && (
        <Kanji style={{ fontSize: 16, letterSpacing: 0, fontWeight: 800, color: kanjiColor }}>{kanji}</Kanji>
      )}
      {children}
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      {right}
    </h2>
  );
}

export function Card({ children, style, as: Tag = 'section', ...rest }: {
  children: ReactNode; style?: CSSProperties; as?: 'section' | 'div' | 'article' | 'ul' | 'ol';
  'aria-label'?: string;
}) {
  return (
    <Tag
      {...rest}
      style={{
        margin: '0 20px',
        background: 'var(--card)',
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: 18,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

/** Selo 済 (círculo vermelhão girado −10°). */
export function Stamp({ size = 30, label = 'Feito', style, char = '済' }: {
  size?: number; label?: string; style?: CSSProperties; char?: string;
}) {
  return (
    <span
      role="img"
      aria-label={label}
      lang="ja"
      style={{
        flex: 'none',
        width: size,
        height: size,
        borderRadius: 999,
        border: '1.5px solid var(--shu)',
        color: 'var(--shu)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-serif)',
        fontSize: Math.round(size * 0.47),
        fontWeight: 800,
        transform: 'rotate(-10deg)',
        ...style,
      }}
    >
      {char}
    </span>
  );
}

export type ButtonVariant = 'ink' | 'indigo' | 'study' | 'water' | 'shu' | 'outline' | 'ghost' | 'good';

const btnColors: Record<ButtonVariant, CSSProperties> = {
  ink: { background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)' },
  indigo: { background: 'var(--indigo)', color: 'var(--paper)', border: '1px solid var(--indigo)' },
  study: { background: 'var(--study)', color: 'var(--paper)', border: '1px solid var(--study)' },
  water: { background: 'var(--water)', color: 'var(--paper)', border: '1px solid var(--water)' },
  shu: { background: 'var(--shu)', color: 'var(--paper)', border: '1px solid var(--shu)' },
  good: { background: 'var(--good-dark)', color: 'var(--paper)', border: '1px solid var(--good-dark)' },
  outline: { background: 'transparent', color: 'var(--ink)', border: '1px solid var(--border)' },
  ghost: { background: 'transparent', color: 'var(--ink)', border: '0' },
};

export function buttonStyle(variant: ButtonVariant = 'ink', extra?: CSSProperties): CSSProperties {
  return {
    minHeight: 44,
    padding: '0 16px',
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    textDecoration: 'none',
    cursor: 'pointer',
    ...btnColors[variant],
    ...extra,
  };
}

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; block?: boolean }
>(function Button({ variant = 'ink', block, style, type = 'button', disabled, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      {...rest}
      style={buttonStyle(variant, {
        width: block ? '100%' : undefined,
        opacity: disabled ? 0.5 : 1,
        ...style,
      })}
    />
  );
});

export function LinkButton({ to, variant = 'ink', style, children, ...rest }: {
  to: string; variant?: ButtonVariant; style?: CSSProperties; children: ReactNode; 'aria-label'?: string;
}) {
  return (
    <Link to={to} {...rest} style={buttonStyle(variant, style)}>
      {children}
    </Link>
  );
}

export function Chip({ children, tone = 'ink', style }: {
  children: ReactNode; tone?: 'ink' | 'shu' | 'indigo' | 'study' | 'water' | 'warn' | 'good' | 'muted'; style?: CSSProperties;
}) {
  const c: Record<string, CSSProperties> = {
    ink: { background: 'var(--ink)', color: 'var(--paper)' },
    shu: { background: 'var(--shu-tint)', color: 'var(--shu-dark)' },
    indigo: { background: 'var(--indigo-tint)', color: 'var(--indigo)' },
    study: { background: 'var(--study-tint)', color: 'var(--study-dark)' },
    water: { background: 'var(--water-tint)', color: 'var(--water-dark)' },
    warn: { background: 'var(--warn-bg)', color: 'var(--warn-title)' },
    good: { background: 'var(--good-bg)', color: 'var(--good-dark)' },
    muted: { background: 'var(--divider)', color: 'var(--muted)' },
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        ...c[tone],
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/** Toggle 46 × 26: trilho tinta e botão vermelhão quando ligado. */
export function Toggle({ on, onChange, label, disabled }: {
  on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{
        flex: 'none',
        width: 46,
        height: 26,
        borderRadius: 999,
        border: 0,
        padding: 3,
        background: on ? 'var(--ink)' : 'var(--border)',
        display: 'flex',
        justifyContent: on ? 'flex-end' : 'flex-start',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'background .15s',
        // alvo de toque de 44 px sem mudar o desenho
        outlineOffset: 4,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 999,
          background: on ? 'var(--shu)' : 'var(--card)',
          boxShadow: on ? 'none' : '0 1px 2px rgba(0,0,0,.12)',
        }}
      />
    </button>
  );
}

/** − valor + (kg com passo 2,5; repetições com passo 1). */
export function Stepper({ value, onChange, step, min = 0, max = 999, format, label, tone = 'ink' }: {
  value: number; onChange: (v: number) => void; step: number; min?: number; max?: number;
  format?: (v: number) => string; label: string; tone?: 'ink' | 'indigo';
}) {
  const color = tone === 'indigo' ? 'var(--indigo)' : 'var(--ink)';
  const btn: CSSProperties = {
    width: 44,
    height: 44,
    flex: 'none',
    border: `1px solid ${tone === 'indigo' ? 'var(--indigo-border)' : 'var(--border)'}`,
    borderRadius: 6,
    background: 'var(--card)',
    color,
    fontSize: 20,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button type="button" aria-label={`Menos ${label}`} style={btn} onClick={() => onChange(Math.max(min, +(value - step).toFixed(2)))}>
        −
      </button>
      <span
        aria-live="polite"
        style={{ minWidth: 52, textAlign: 'center', fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 800, color }}
      >
        {format ? format(value) : value}
      </span>
      <button type="button" aria-label={`Mais ${label}`} style={btn} onClick={() => onChange(Math.min(max, +(value + step).toFixed(2)))}>
        +
      </button>
    </div>
  );
}

/** Anel SVG com stroke-dasharray (estudo r = 96 → 603,19; corrida r = 100 → 628,32). */
export function Ring({ size = 220, r = 96, stroke = 10, progress, color, track = 'var(--line)', children }: {
  size?: number; r?: number; stroke?: number; progress: number; color: string; track?: string; children?: ReactNode;
}) {
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(1, progress));
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={c.toFixed(2)}
          strokeDashoffset={(c * (1 - p)).toFixed(2)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset .4s linear' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function ProgressBar({ value, color = 'var(--ink)', height = 4, track = 'var(--line)', style }: {
  value: number; color?: string; height?: number; track?: string; style?: CSSProperties;
}) {
  return (
    <div style={{ height, background: track, ...style }}>
      <div style={{ width: `${Math.max(0, Math.min(100, value * 100))}%`, height: '100%', background: color }} />
    </div>
  );
}

export function BigNumber({ value, unit, size = 46, color, unitStyle }: {
  value: ReactNode; unit?: ReactNode; size?: number; color?: string; unitStyle?: CSSProperties;
}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: size, lineHeight: 1, fontWeight: 800, color }}>{value}</span>
      {unit && (
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--muted)', ...unitStyle }}>{unit}</span>
      )}
    </span>
  );
}

export const TONES: Record<AlertTone, { bg: string; bd: string; ic: string; t: string; x: string }> = {
  warn: { bg: '#F4E8CF', bd: '#E9D7AE', ic: '#9A5B00', t: '#6E4300', x: '#5C4520' },
  gym: { bg: '#E4E8EF', bd: '#D3DAE6', ic: '#2C4A78', t: '#1F3459', x: '#34465F' },
  study: { bg: '#EAE6F0', bd: '#DAD3E6', ic: '#5A4A86', t: '#43366A', x: '#4A4166' },
  water: { bg: '#E2ECEA', bd: '#CFE0DD', ic: '#2B6E70', t: '#1E5153', x: '#2F4A4B' },
  good: { bg: '#E6EAD8', bd: '#D3DABD', ic: '#3A5121', t: '#3A5121', x: '#3F4A30' },
  info: { bg: '#FBFAF6', bd: '#E3DED3', ic: '#1B1A17', t: '#1B1A17', x: '#4A463F' },
};

/** Cartão de aviso: fundo e borda pelo tom, quadrado de 34 px com o kanji, título, texto e ação. */
export function AlertCard({ tone, kanji, title, body, action, style }: {
  tone: AlertTone; kanji: string; title: ReactNode; body: ReactNode;
  action?: { label: string; to?: string; onClick?: () => void; disabled?: boolean } | null;
  style?: CSSProperties;
}) {
  const k = TONES[tone];
  const linkStyle: CSSProperties = {
    alignSelf: 'flex-start',
    marginTop: 4,
    fontSize: 13,
    fontWeight: 700,
    color: k.t,
    background: 'none',
    border: 0,
    padding: 0,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    minHeight: 28,
    cursor: 'pointer',
  };
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        padding: 14,
        borderRadius: 8,
        background: k.bg,
        border: `1px solid ${k.bd}`,
        ...style,
      }}
    >
      <span
        lang="ja"
        aria-hidden="true"
        style={{
          flex: 'none',
          width: 34,
          height: 34,
          borderRadius: 5,
          border: `1.5px solid ${k.ic}`,
          color: k.ic,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-serif)',
          fontSize: 17,
          fontWeight: 800,
        }}
      >
        {kanji}
      </span>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <strong style={{ fontSize: 15, color: k.t }}>{title}</strong>
        <span style={{ fontSize: 13.5, lineHeight: 1.45, color: k.x }}>{body}</span>
        {action &&
          (action.to ? (
            <Link to={action.to} style={linkStyle}>
              {action.label} →
            </Link>
          ) : (
            <button type="button" onClick={action.onClick} disabled={action.disabled} style={linkStyle}>
              {action.label} →
            </button>
          ))}
      </div>
    </div>
  );
}

/** Linha de lista (chave à esquerda, valor à direita) usada no Perfil e em resumos. */
export function KeyValueList({ rows, wrapKey }: { rows: { k: ReactNode; v: ReactNode }[]; wrapKey?: boolean }) {
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: '0 20px',
        padding: 0,
        background: 'var(--card)',
        border: '1px solid var(--line)',
        borderRadius: 8,
      }}
    >
      {rows.map((r, i) => (
        <li
          key={i}
          style={{
            minHeight: 52,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            borderBottom: i < rows.length - 1 ? '1px solid var(--divider)' : undefined,
          }}
        >
          <span style={{ flex: wrapKey ? 'none' : 1, fontSize: 14.5, lineHeight: 1.4, fontWeight: 700 }}>{r.k}</span>
          <span style={{ flex: wrapKey ? 1 : undefined, fontSize: 14, lineHeight: 1.4, fontWeight: 500, color: 'var(--ink-2)', textAlign: 'right' }}>
            {r.v}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function Spinner({ label = 'Carregando' }: { label?: string }) {
  return (
    <div role="status" aria-label={label} style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
      <span
        lang="ja"
        aria-hidden="true"
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 28,
          fontWeight: 800,
          color: 'var(--shu)',
          animation: 'kz-pulse 1.2s ease-in-out infinite',
        }}
      >
        改
      </span>
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : 'Algo deu errado';
  return (
    <div style={{ margin: 20 }}>
      <AlertCard
        tone="warn"
        kanji="注"
        title="Não deu pra carregar"
        body={msg}
        action={onRetry ? { label: 'Tentar de novo', onClick: onRetry } : null}
      />
    </div>
  );
}

/** Indicador "aguardando sinal" (9.4): ponto ocre. */
export function PendingDot({ label = 'aguardando sinal' }: { label?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--warn-icon)' }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--warn-icon)' }} />
      {label}
    </span>
  );
}
